/**
 * <product-matrix> — Meerkleding productpagina.
 *
 * De section rendert de huidige kleur volledig in Liquid; dit component neemt het daarna over:
 * kleuren wisselen zonder paginalading, aantallen per maat, staffel (selectie + winkelwagen van
 * hetzelfde model), btw-schakelaar, blanco/bedrukken, samenvatting, vaste balk op mobiel en
 * één /cart/add.js met alle regels. De winkelwagen-drawer wordt bijgewerkt via Shopify's
 * standaard CartLinesUpdateEvent (dezelfde route als Horizon's eigen product-form).
 *
 * Prijzen: de kassa (SupaEasy) trekt de staffelkorting PER STUK af, naar beneden afgerond op
 * centen. Stukprijs = prijs − floor(prijs × pct / 100). € 3,25 bij 5% = € 3,09.
 */

const BTW = 1.21;
const OPSLAG_BTW = 'mk_vat_mode';

class ProductMatrix extends HTMLElement {
  connectedCallback() {
    const bron = this.querySelector('[data-pm-data]');
    if (!bron) return;
    try {
      this.data = JSON.parse(bron.textContent);
    } catch (e) {
      console.error('product-matrix: data onleesbaar', e);
      return;
    }

    this.entries = this.data.entries.filter((e) => e.varianten.length > 0);
    this.entries.sort((a, b) => a.naam.localeCompare(b.naam, 'nl'));
    // Maten op grootte, niet in de volgorde uit Shopify (die staat soms alfabetisch: 3XL, 4XL, L, M, S …)
    for (const e of this.entries) e.varianten = ProductMatrix.sorteerMaten(e.varianten);
    this.perVariant = new Map();
    for (const e of this.entries) for (const v of e.varianten) this.perVariant.set(v.id, { entry: e, variant: v });
    this.groepProductIds = new Set(this.entries.map((e) => e.productId));

    this.huidig = this.entries.find((e) => e.key === this.data.huidig) || this.entries[0];
    this.opslagSleutel = 'mk_pm_' + (this.data.staffelGroep || this.dataset.productId);
    this.aantallen = this.#laadSelectie();
    this.btw = localStorage.getItem(OPSLAG_BTW) === 'ex' ? 'ex' : 'in';
    this.modus = 'blanco';
    this.posities = new Set(
      [...this.querySelectorAll('[data-pm-positie][aria-pressed="true"]')].map((b) => b.dataset.pmPositie)
    );
    this.inWagen = this.data.inWagen || 0;
    this.wagenTotaal = this.data.wagenTotaal || 0;
    this.drempel = parseInt(this.dataset.voorraadDrempel, 10) || 50;
    this.bedrukMin = parseInt(this.dataset.bedrukMin, 10) || 10;
    this.fouten = new Map();

    this.addEventListener('click', this.#opKlik);
    this.addEventListener('change', this.#opWijziging);
    this.addEventListener('keydown', this.#opToets);
    this.#volgSamenvatting();
    this.render();
  }

  /* ───────────── maatvolgorde ───────────── */

  /**
   * Maten op grootte, met één schema voor alle maten van het product (zelfde regel als
   * includes/maatvolgorde.php in de backoffice):
   *   A. elke maat begint met een lettermaat (S, M, "L (41/42)", "S/M")
   *   B. elke maat heeft een lettermaat tussen haakjes ("98/104 (XS) | 3-4jr", "7/8 (M)")
   *   C. elke maat begint met een getal van dezelfde soort (allemaal cm/maat, allemaal maanden of allemaal jaren)
   * Past geen schema (gemengd), dan blijft de volgorde uit Shopify staan.
   */
  static sorteerMaten(varianten) {
    const L = { XXXS: 1, '3XS': 1, XXS: 2, '2XS': 2, XS: 3, S: 4, M: 5, L: 6, XL: 7, XXL: 8, '2XL': 8, XXXL: 9, '3XL': 9, XXXXL: 10, '4XL': 10, '5XL': 11, '6XL': 12, '7XL': 13, '8XL': 14 };
    const voor = (m) => L[String(m).trim().toUpperCase().split(/[\s(/]/)[0]];
    const haakjes = (m) => { const r = String(m).match(/\(\s*([2-8]?X{0,4}[SML]|X{1,4}L|[2-8]XL)\s*\)/i); return r ? L[r[1].toUpperCase()] : undefined; };
    const getal = (m) => { const r = String(m).match(/^\s*(\d+(?:[.,]\d+)?)/); return r ? parseFloat(r[1].replace(',', '.')) : undefined; };
    const soort = (m) => { const l = String(m).toLowerCase(); if (/month|monate|maand|mnd/.test(l)) return 'maand'; if (/year|jahre|jaar|\bjr\b|\djr/.test(l)) return 'jaar'; return 'getal'; };
    const maten = varianten.map((v) => v.maat);
    let sleutels = maten.map(voor);
    if (sleutels.includes(undefined)) sleutels = maten.map(haakjes);
    if (sleutels.includes(undefined)) sleutels = new Set(maten.map(soort)).size === 1 ? maten.map(getal) : [undefined];
    if (sleutels.includes(undefined)) return varianten;
    return varianten.map((v, i) => ({ v, i, k: sleutels[i] })).sort((a, b) => a.k - b.k || a.i - b.i).map((x) => x.v);
  }

  /* ───────────── rekenen ───────────── */

  geld(centen) {
    const fmt = this.data.moneyFormat || '€ {{amount_with_comma_separator}}';
    const bedrag = (centen / 100).toFixed(2);
    const [heel, dec] = bedrag.split('.');
    const punten = heel.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return fmt.replace(/{{\s*(\w+)\s*}}/, (_, soort) => {
      switch (soort) {
        case 'amount': return heel.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + dec;
        case 'amount_no_decimals': return heel;
        case 'amount_no_decimals_with_comma_separator': return punten;
        default: return punten + ',' + dec; // amount_with_comma_separator
      }
    });
  }

  /** Bedrag in de gekozen weergave (particulier incl., zakelijk excl.). */
  toon(centen) {
    return this.btw === 'ex' ? Math.round(centen / BTW) : centen;
  }

  get treden() {
    return (this.data.treden || []).slice().sort((a, b) => a.min - b.min);
  }

  kortingVoor(stuks) {
    if (!this.data.staffelGroep) return 0;
    let pct = 0;
    for (const t of this.treden) if (stuks >= t.min) pct = t.pct;
    return pct;
  }

  stukprijs(prijs, pct) {
    return prijs - Math.floor((prijs * pct) / 100);
  }

  get selectieStuks() {
    let n = 0;
    for (const q of this.aantallen.values()) n += q;
    return n;
  }

  get teller() {
    return this.selectieStuks + (this.data.staffelGroep ? this.inWagen : 0);
  }

  /* ───────────── opslag ───────────── */

  #laadSelectie() {
    const m = new Map();
    try {
      const o = JSON.parse(sessionStorage.getItem(this.opslagSleutel) || '{}');
      for (const [id, q] of Object.entries(o)) if (this.perVariant.has(Number(id)) && q > 0) m.set(Number(id), q);
    } catch (e) {}
    return m;
  }

  #bewaarSelectie() {
    try {
      sessionStorage.setItem(this.opslagSleutel, JSON.stringify(Object.fromEntries(this.aantallen)));
    } catch (e) {}
  }

  zet(variantId, aantal) {
    const info = this.perVariant.get(variantId);
    if (!info) return;
    let q = Math.max(0, Math.min(9999, Math.floor(Number(aantal) || 0)));
    this.fouten.delete(variantId);
    if (!info.variant.beschikbaar) q = 0;
    if (info.variant.voorraad !== null && q > info.variant.voorraad) {
      q = Math.max(0, info.variant.voorraad);
      this.fouten.set(variantId, `Max. ${q} op voorraad`);
    }
    if (q > 0) this.aantallen.set(variantId, q);
    else this.aantallen.delete(variantId);
    this.#bewaarSelectie();
  }

  /* ───────────── events ───────────── */

  #opKlik = (ev) => {
    const t = ev.target.closest('button, a');
    if (!t || !this.contains(t)) return;

    if (t.closest('.pm-stap') && t.tagName === 'BUTTON') {
      const input = t.parentElement.querySelector('input');
      const id = Number(input.dataset.variant);
      const d = t === t.parentElement.firstElementChild ? -1 : 1;
      this.zet(id, (this.aantallen.get(id) || 0) + d);
      this.render();
      return;
    }
    if (t.dataset.pmKleur) {
      ev.preventDefault();
      this.kiesKleur(t.dataset.pmKleur);
      return;
    }
    if (t.dataset.pmBtw) {
      this.btw = t.dataset.pmBtw;
      localStorage.setItem(OPSLAG_BTW, this.btw);
      this.render();
      return;
    }
    if (t.dataset.pmModus) {
      this.modus = t.dataset.pmModus;
      this.render();
      return;
    }
    if (t.dataset.pmPositie) {
      const p = t.dataset.pmPositie;
      this.posities.has(p) ? this.posities.delete(p) : this.posities.add(p);
      this.render();
      return;
    }
    if (t.hasAttribute('data-pm-cta') || t.hasAttribute('data-pm-m-cta')) {
      if (t.hasAttribute('data-pm-m-cta') && this.selectieStuks === 0) {
        this.querySelector('[data-pm-matrix]').scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      this.modus === 'bedrukken' ? this.naarOfferte() : this.inWinkelmand();
      return;
    }
    if (t.hasAttribute('data-pm-vv-open') || t.dataset.pmVvVariant) {
      this.#openVoorraadvraag(t.dataset.pmVvVariant);
      return;
    }
    if (t.dataset.pmOpenTab) {
      this.openTab(t.dataset.pmOpenTab, true);
      return;
    }
    if (t.getAttribute('role') === 'tab') {
      this.openTab(t.id.replace('pm-tab-', ''));
      return;
    }
    if (t.classList.contains('pm-duim')) {
      this.#toonBeeld(t.dataset.src, t.querySelector('img')?.alt);
      this.querySelectorAll('.pm-duim').forEach((d) => d.setAttribute('aria-pressed', String(d === t)));
    }
  };

  #opWijziging = (ev) => {
    const input = ev.target.closest('.pm-stap input');
    if (!input) return;
    this.zet(Number(input.dataset.variant), input.value);
    this.render();
  };

  #opToets = (ev) => {
    const tab = ev.target.closest('[role="tab"]');
    if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(ev.key)) return;
    const tabs = [...this.querySelectorAll('[role="tab"]')];
    let i = tabs.indexOf(tab);
    if (ev.key === 'ArrowLeft') i = (i - 1 + tabs.length) % tabs.length;
    if (ev.key === 'ArrowRight') i = (i + 1) % tabs.length;
    if (ev.key === 'Home') i = 0;
    if (ev.key === 'End') i = tabs.length - 1;
    ev.preventDefault();
    this.openTab(tabs[i].id.replace('pm-tab-', ''));
    tabs[i].focus();
  };

  openTab(naam, scroll = false) {
    for (const tab of this.querySelectorAll('[role="tab"]')) {
      const aan = tab.id === 'pm-tab-' + naam;
      tab.setAttribute('aria-selected', String(aan));
      tab.tabIndex = aan ? 0 : -1;
      const paneel = this.querySelector('#' + tab.getAttribute('aria-controls'));
      if (paneel) paneel.hidden = !aan;
    }
    if (scroll) this.querySelector('.pm-details')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  kiesKleur(key) {
    const e = this.entries.find((x) => x.key === key);
    if (!e || e === this.huidig) return;
    this.huidig = e;
    // Fotolabel "Per stuk geleverd" volgt het metaveld van de gekozen kleur (custom.per_stuk_melding)
    const perStuk = this.querySelector('[data-pm-perstuk]');
    if (perStuk) perStuk.hidden = !e.perStuk;
    try {
      history.replaceState(history.state, '', e.url);
    } catch (err) {}
    if (e.titel) document.title = document.title.replace(/^[^|–-]+/, e.titel + ' ');
    this.render();
  }

  /* ───────────── tekenen ───────────── */

  render() {
    const pct = this.kortingVoor(this.teller);
    this.#tekenPrijs(pct);
    this.#tekenStaffel(pct);
    this.#tekenKleuren();
    this.#tekenMatrix(pct);
    this.#tekenGalerij();
    this.#tekenVoorraadvraag();
    this.#tekenModus();
    this.#tekenSamenvatting(pct);
  }

  #tekenPrijs(pct) {
    const basis = Math.min(...this.huidig.varianten.map((v) => v.prijs));
    const q = (s) => this.querySelector(s);
    q('[data-pm-stukprijs]').textContent = this.geld(this.toon(this.stukprijs(basis, pct)));
    q('[data-pm-stukprijs-label]').textContent = `per stuk ${this.btw === 'ex' ? 'excl.' : 'incl.'} btw`;
    // Van-prijs: een prijsactie (compare_at_price, uit het backoffice) en/of de staffelkorting
    const goedkoopste = this.huidig.varianten.find((v) => v.prijs === basis);
    const van = goedkoopste && goedkoopste.van ? goedkoopste.van : null;
    const was = q('[data-pm-was]');
    was.hidden = pct === 0 && !van;
    was.textContent = this.geld(this.toon(van || basis));
    const actie = q('[data-pm-actie]');
    if (actie) {
      actie.hidden = !van;
      // Labelkleur volgt de kleurgroep van de actie (paarse actie = paars label); zie mk-base.css.
      // Categorie-actie (shop-metaveld sleutel "cat-<categorie>", bv. Black Friday-weekpromo) = huisstijlblauw.
      const catNaam = this.data.actieLabels && this.data.actieCat ? this.data.actieLabels[this.data.actieCat] : null;
      actie.className = `pm-actie mk-actie${catNaam ? ' mk-actie--actie' : goedkoopste && goedkoopste.fk ? ` mk-actie--${goedkoopste.fk}` : ''}`;
      if (van) {
        const pctTekst = `−${Math.floor((1 - basis / van) * 100 + 1e-9)}%`; // naar beneden: nooit meer beloven dan er is
        // Naam van de actie uit het backoffice (shop-metaveld custom.prijsacties), bv. "Paarse vrijdag −20%"
        const naam = catNaam || (goedkoopste && goedkoopste.fk && this.data.actieLabels ? this.data.actieLabels[goedkoopste.fk] : null);
        actie.textContent = naam ? `${naam} ${pctTekst}` : `Actie ${pctTekst}`;
      }
    }
    for (const b of this.querySelectorAll('[data-pm-btw]')) b.setAttribute('aria-pressed', String(b.dataset.pmBtw === this.btw));
  }

  #tekenStaffel(pct) {
    const blok = this.querySelector('[data-pm-treden]');
    if (!blok) return;
    const basis = Math.min(...this.huidig.varianten.map((v) => v.prijs));
    const tr = this.treden;
    const rijen = [{ van: 1, tot: tr[0].min - 1, pct: 0 }];
    tr.forEach((t, i) => rijen.push({ van: t.min, tot: tr[i + 1] ? tr[i + 1].min - 1 : null, pct: t.pct }));
    const n = this.teller;
    blok.innerHTML = rijen
      .map((r) => {
        const aan = n > 0 && r.pct === pct;
        const label = r.tot === null ? `${r.van}+ st.` : r.van === r.tot ? `${r.van} st.` : `${r.van}–${r.tot} st.`;
        return `<div class="pm-trede${aan ? ' is-actief' : ''}"><div class="q">${label}</div><div class="p mk-num">${this.geld(
          this.toon(this.stukprijs(basis, r.pct))
        )}</div><div class="d">${r.pct ? '−' + r.pct + '%' : '&nbsp;'}</div></div>`;
      })
      .join('');

    const volgende = tr.find((t) => t.min > n);
    const vorigeMin = [...tr].reverse().find((t) => t.min <= n)?.min || 0;
    const wagen = this.inWagen > 0 ? ` (incl. ${this.inWagen} in je winkelwagen)` : '';
    let tekst, breedte;
    if (volgende) {
      const nodig = volgende.min - n;
      breedte = Math.max(3, Math.min(100, ((n - vorigeMin) / (volgende.min - vorigeMin)) * 100));
      tekst = `Nog <b>${nodig} ${nodig === 1 ? 'stuk' : 'stuks'}</b> tot ${volgende.pct}% korting — in elke maat of kleur.`;
      if (n > 0) tekst = `${n} stuks${wagen}. ` + tekst;
    } else {
      breedte = 100;
      tekst = `<b>${n} stuks${wagen}</b> — je krijgt de maximale staffelkorting van ${pct}%.`;
    }
    this.querySelector('[data-pm-voortgang]').innerHTML = `${tekst}<div class="pm-balk"><i style="width:${breedte}%"></i></div>`;
  }

  #stuksVan(entry) {
    return entry.varianten.reduce((s, v) => s + (this.aantallen.get(v.id) || 0), 0);
  }

  #tekenKleuren() {
    const blok = this.querySelector('[data-pm-kleurblok]');
    const lijst = this.querySelector('[data-pm-kleuren]');
    if (this.entries.length < 2) {
      if (blok) blok.hidden = this.entries.length < 2 && !this.huidig.naam;
      if (lijst) lijst.hidden = true;
    }
    if (lijst && this.entries.length >= 2) {
      lijst.innerHTML = this.entries
        .map((e, i) => {
          const q = this.#stuksVan(e);
          const op = !e.varianten.some((v) => v.beschikbaar);
          const naam = this.#esc(e.naam);
          return `<a href="${e.url}" class="pm-kleur${op ? ' pm-kleur--op' : ''}" data-pm-kleur="${this.#esc(e.key)}" role="button" aria-pressed="${
            e === this.huidig
          }" title="${naam}${op ? ' (uitverkocht)' : ''}" aria-label="${naam}${q ? `, ${q} stuks gekozen` : ''}${op ? ', uitverkocht' : ''}">${
            e.thumb ? `<img src="${e.thumb}" alt="" width="52" height="52" loading="lazy">` : ''
          }${q ? `<span class="pm-kleur__aantal">${q}</span>` : ''}</a>`;
        })
        .join('');
    }
    const naamEl = this.querySelector('[data-pm-kleurnaam]');
    if (naamEl) naamEl.textContent = this.huidig.naam;
    const titelEl = this.querySelector('[data-pm-kleurtitel]');
    if (titelEl) titelEl.textContent = this.huidig.naam;
    const telling = this.querySelector('[data-pm-kleurtelling]');
    if (telling) {
      const gebruikt = this.entries.filter((e) => this.#stuksVan(e) > 0).length;
      telling.textContent =
        gebruikt > 1 ? `${gebruikt} kleuren in je selectie` : this.entries.length > 1 ? `${this.entries.length} kleuren · mixen mag` : '';
    }
  }

  #tekenMatrix(pct) {
    const matrix = this.querySelector('[data-pm-matrix]');
    const basis = Math.min(...this.huidig.varianten.map((v) => v.prijs));
    const html = this.huidig.varianten
      .map((v) => {
        const q = this.aantallen.get(v.id) || 0;
        const maat = this.#esc(v.maat);
        const fout = this.fouten.get(v.id);
        let extra = '';
        if (!v.beschikbaar) extra = '';
        else if (fout) extra = `<div class="pm-cel__fout">${fout}</div>`;
        else if (v.voorraad !== null && v.voorraad < this.drempel) extra = `<div class="pm-cel__nog">Nog ${v.voorraad}</div>`;
        // Ruim op voorraad ook benoemen: zonder regel dachten klanten dat de maat uitverkocht was
        else extra = `<div class="pm-cel__nog pm-cel__nog--ruim">Op voorraad</div>`;
        const prijs = v.beschikbaar
          ? `<div class="pm-cel__prijs mk-num${v.prijs > basis ? ' is-groot' : ''}"${v.prijs > basis ? ' title="Grote maat"' : ''}>${
              v.van ? `<s>${this.geld(this.toon(v.van))}</s> ` : ''
            }${this.geld(this.toon(this.stukprijs(v.prijs, pct)))}</div>`
          : `<button type="button" class="pm-cel__prijs pm-cel__op" data-pm-vv-variant="${v.id}">Uitverkocht · wanneer weer?</button>`;
        const dis = v.beschikbaar ? '' : ' disabled';
        return `<div class="pm-cel${q ? ' is-gevuld' : ''}${fout ? ' is-fout' : ''}${v.beschikbaar ? '' : ' pm-cel--op'}"><div><div class="pm-cel__maat">${maat}${v.sku ? `<span class="pm-cel__sku" title="SKU">${this.#esc(v.sku)}</span>` : ''}</div>${prijs}${extra}</div><div class="pm-stap"><button type="button" aria-label="${maat} minder"${
          dis || (q ? '' : ' disabled')
        }>−</button><input type="number" inputmode="numeric" min="0" max="9999" value="${q}" aria-label="Aantal ${maat}" data-variant="${v.id}"${dis}><button type="button" aria-label="${maat} meer"${dis}>+</button></div></div>`;
      })
      .join('');
    // Focus bewaren als je in een invoerveld typt/klikt
    const actief = document.activeElement;
    const focusId = actief && matrix.contains(actief) ? (actief.dataset.variant || actief.getAttribute('aria-label')) : null;
    matrix.innerHTML = html;
    if (focusId) {
      const terug = [...matrix.querySelectorAll('input, button')].find(
        (el) => el.dataset.variant === focusId || el.getAttribute('aria-label') === focusId
      );
      terug?.focus();
    }
  }

  #tekenGalerij() {
    if (this.#getoondeEntry === this.huidig) return;
    this.#getoondeEntry = this.huidig;
    const beelden = this.huidig.beelden || [];
    if (beelden[0]) this.#toonBeeld(beelden[0].src, beelden[0].alt);
    const duimen = this.querySelector('[data-pm-duimen]');
    if (!duimen) return;
    if (beelden.length < 2) {
      duimen.innerHTML = '';
      return;
    }
    duimen.innerHTML = beelden
      .map(
        (b, i) =>
          `<button type="button" class="pm-duim" aria-pressed="${i === 0}" data-src="${b.src}" aria-label="Afbeelding ${i + 1}"><img src="${this.#breedte(
            b.src,
            160
          )}" alt="${this.#esc(b.alt || '')}" width="64" height="64" loading="lazy"></button>`
      )
      .join('');
  }
  #getoondeEntry = null;

  /* ───────────── "Wanneer is er weer voorraad?" (backoffice voorraadvraag/api.php) ───────────── */

  #tekenVoorraadvraag() {
    const blok = this.querySelector('[data-pm-vv]');
    if (!blok) return;
    const op = this.huidig.varianten.filter((v) => !v.beschikbaar);
    blok.hidden = op.length === 0;
    const kies = blok.querySelector('[data-pm-vv-maat]');
    const huidig = kies.value;
    kies.innerHTML = op.map((v) => `<option value="${v.id}">${this.#esc(v.maat)}</option>`).join('');
    if (op.some((v) => String(v.id) === huidig)) kies.value = huidig;
    const zonderMaten = this.huidig.varianten.every((v) => v.maat === 'Aantal');
    kies.hidden = zonderMaten || op.length === 1;
    const link = blok.querySelector('[data-pm-vv-open]');
    if (link) link.textContent = zonderMaten ? 'Uitverkocht? Wanneer is er weer voorraad?' : 'Maat uitverkocht? Wanneer is er weer voorraad?';
  }

  #openVoorraadvraag(variantId) {
    const blok = this.querySelector('[data-pm-vv]');
    const form = blok?.querySelector('[data-pm-vv-form]');
    if (!form) return;
    form.hidden = false;
    if (variantId) form.querySelector('[data-pm-vv-maat]').value = variantId;
    if (!form.dataset.gekoppeld) {
      form.dataset.gekoppeld = '1';
      form.addEventListener('submit', (ev) => this.#verstuurVoorraadvraag(ev, form));
    }
    form.querySelector('input[type="email"]').focus();
  }

  async #verstuurVoorraadvraag(ev, form) {
    ev.preventDefault();
    const melding = form.querySelector('[data-pm-vv-melding]');
    const knop = form.querySelector('button[type="submit"]');
    const data = new FormData(form);
    data.set('url', location.href);
    knop.disabled = true;
    try {
      const res = await fetch('https://backoffice.gibreto.nl/voorraadvraag/api.php', { method: 'POST', body: data });
      const j = await res.json();
      melding.textContent = j.melding || (j.ok ? 'Bedankt! We laten het je weten.' : 'Dat lukte niet, probeer het later nog eens.');
      melding.classList.toggle('is-fout', !j.ok);
    } catch (e) {
      melding.textContent = 'Dat lukte niet. Mail ons gerust via info@meerkleding.nl.';
      melding.classList.add('is-fout');
    }
    melding.hidden = false;
    knop.disabled = false;
  }

  #breedte(src, w) {
    return /[?&]width=\d+/.test(src) ? src.replace(/([?&]width=)\d+/, `$1${w}`) : src + (src.includes('?') ? '&' : '?') + 'width=' + w;
  }

  #toonBeeld(src, alt) {
    const img = this.querySelector('[data-pm-hoofdbeeld]');
    if (!img || !src) return;
    img.removeAttribute('srcset');
    img.srcset = [400, 600, 800, 1000].map((w) => `${this.#breedte(src, w)} ${w}w`).join(', ');
    img.src = this.#breedte(src, 1000);
    if (alt) img.alt = alt;
  }

  #tekenModus() {
    for (const b of this.querySelectorAll('[data-pm-modus]')) b.setAttribute('aria-pressed', String(b.dataset.pmModus === this.modus));
    for (const b of this.querySelectorAll('[data-pm-positie]')) b.setAttribute('aria-pressed', String(this.posities.has(b.dataset.pmPositie)));
    const opties = this.querySelector('[data-pm-bedrukopties]');
    if (opties) opties.hidden = this.modus !== 'bedrukken';
  }

  #tekenSamenvatting(pct) {
    const n = this.selectieStuks;
    let totaal = 0;
    const regels = [];
    for (const e of this.entries) {
      const delen = [];
      let sub = 0;
      for (const v of e.varianten) {
        const q = this.aantallen.get(v.id);
        if (!q) continue;
        delen.push(`${q}×${this.#esc(v.maat === 'Aantal' ? '' : v.maat)}`.replace(/×$/, ''));
        sub += q * this.stukprijs(v.prijs, pct);
      }
      if (!delen.length) continue;
      totaal += sub;
      regels.push(
        `<div class="pm-regel"><span>${e.thumb ? `<img src="${e.thumb}" alt="">` : ''}${this.#esc(e.naam)} · ${delen.join(', ')}</span><span class="mk-num">${this.geld(
          this.toon(sub)
        )}</span></div>`
      );
    }
    if (pct && n) regels.push(`<div class="pm-regel pm-regel--korting"><span>Staffelkorting ${pct}% verwerkt</span><span></span></div>`);
    const q = (s) => this.querySelector(s);
    q('[data-pm-regels]').innerHTML =
      regels.join('') ||
      `<div class="pm-regel pm-dim">Kies hierboven je ${this.huidig.varianten.some((v) => v.maat !== 'Aantal') ? 'maten en aantallen' : 'aantal'}.</div>`;
    const stuksTekst = `${n} ${n === 1 ? 'stuk' : 'stuks'}`;
    const btwTekst = this.btw === 'ex' ? 'excl. btw' : 'incl. btw';
    q('[data-pm-aantal]').textContent = stuksTekst;
    q('[data-pm-totaal]').textContent = this.geld(this.toon(totaal));
    q('[data-pm-totaal-btw]').textContent = btwTekst;

    const bedrukken = this.modus === 'bedrukken';
    const teWeinig = bedrukken && n < this.bedrukMin;
    const cta = q('[data-pm-cta]');
    cta.textContent = bedrukken ? 'Verder naar prijs voor bedrukken' : 'In winkelmand';
    cta.disabled = n === 0 || teWeinig || (bedrukken && this.posities.size === 0 && this.querySelector('[data-pm-positie]'));
    const waarsch = q('[data-pm-bedrukwaarschuwing]');
    if (waarsch) {
      waarsch.hidden = !teWeinig;
      const nodig = this.bedrukMin - n;
      waarsch.textContent = teWeinig ? `Bedrukken kan vanaf ${this.bedrukMin} stuks — voeg er nog ${nodig} ${nodig === 1 ? 'toe' : 'toe'}.` : '';
    }

    // Gratis verzending: drempel is incl. btw, rekenen altijd incl.; tonen in de gekozen weergave.
    const verzending = q('[data-pm-verzending]');
    const vanaf = this.data.gratisVanaf || 0;
    if (verzending && vanaf > 0 && !bedrukken) {
      const basis = this.wagenTotaal + totaal;
      const nog = vanaf - basis;
      const breedte = Math.max(0, Math.min(100, (basis / vanaf) * 100));
      verzending.innerHTML =
        basis === 0
          ? ''
          : nog > 0
          ? `Nog <b>${this.geld(this.toon(nog))}</b>${this.btw === 'ex' ? ' excl. btw' : ''} tot gratis verzending<div class="pm-balk"><i style="width:${breedte}%"></i></div>`
          : `<span style="color:var(--pm-groen);font-weight:600">✓ Gratis verzending</span>`;
    } else if (verzending) verzending.innerHTML = '';

    const mbalk = q('[data-pm-mbalk]');
    if (mbalk) {
      mbalk.hidden = this.#samenvattingZichtbaar;
      q('[data-pm-m-totaal]').textContent = this.geld(this.toon(totaal));
      q('[data-pm-m-aantal]').textContent = `${stuksTekst} · ${btwTekst}`;
      const mcta = q('[data-pm-m-cta]');
      mcta.textContent = n === 0 ? 'Kies je maten' : bedrukken ? 'Prijs bedrukken' : 'In winkelmand';
      mcta.disabled = n > 0 && cta.disabled;
    }
  }

  #samenvattingZichtbaar = false;
  #volgSamenvatting() {
    const sv = this.querySelector('[data-pm-samenvatting]');
    if (!sv || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(([e]) => {
      this.#samenvattingZichtbaar = e.isIntersecting;
      const mbalk = this.querySelector('[data-pm-mbalk]');
      if (mbalk) mbalk.hidden = e.isIntersecting;
    }).observe(sv);
  }

  #esc(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  #melding(tekst, fout = false) {
    const m = this.querySelector('[data-pm-melding]');
    m.hidden = !tekst;
    m.textContent = tekst || '';
    m.classList.toggle('is-fout', fout);
  }

  /* ───────────── bestellen ───────────── */

  naarOfferte() {
    const n = this.selectieStuks;
    if (n < this.bedrukMin) return;
    // Kleur met de meeste stuks bepaalt het product in de offertetool.
    let beste = null;
    for (const e of this.entries) {
      const q = this.#stuksVan(e);
      if (q && (!beste || q > beste.q)) beste = { e, q };
    }
    const e = beste?.e || this.huidig;
    const variant = e.varianten.find((v) => this.aantallen.get(v.id)) || e.varianten[0];
    const url = new URL(this.dataset.offerteUrl, location.href);
    url.searchParams.set('product', e.productId);
    url.searchParams.set('variant', variant.id);
    url.searchParams.set('aantal', n);
    if (this.posities.size) url.searchParams.set('posities', [...this.posities].join(','));
    window.open(url.toString(), '_blank', 'noopener');
  }

  async inWinkelmand() {
    const items = [...this.aantallen].filter(([, q]) => q > 0).map(([id, quantity]) => ({ id, quantity }));
    if (!items.length) return;
    const knoppen = this.querySelectorAll('[data-pm-cta], [data-pm-m-cta]');
    knoppen.forEach((k) => {
      k.disabled = true;
      k.classList.add('is-bezig');
    });
    this.#melding('');

    let events = null;
    try {
      events = await import('@shopify/events');
    } catch (e) {}
    const uitgesteld = events?.CartLinesUpdateEvent?.createPromise?.();
    if (events && uitgesteld) {
      this.dispatchEvent(
        new events.CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
          promise: uitgesteld.promise,
        })
      );
    }

    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
    const stuks = this.selectieStuks;
    try {
      const res = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(','), sections_url: location.pathname }),
      });
      const antwoord = await res.json();
      if (!res.ok || antwoord.status) {
        throw new Error(antwoord.description || antwoord.message || 'Toevoegen mislukt');
      }
      const wagen = await (await fetch((window.Shopify?.routes?.root || '/') + 'cart.js')).json();

      uitgesteld?.resolve({
        cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse
          ? events.CartLinesUpdateEvent.createCartFromAjaxResponse(wagen)
          : null,
        detail: { items: wagen.items, source: 'product-matrix', itemCount: stuks, sections: antwoord.sections, didError: false },
      });

      this.#analytics(antwoord.items || []);
      this.wagenTotaal = wagen.total_price;
      this.inWagen = wagen.items.filter((i) => this.groepProductIds.has(i.product_id)).reduce((s, i) => s + i.quantity, 0);
      this.aantallen.clear();
      this.#bewaarSelectie();
      this.render();
      this.#melding(`✓ ${stuks} ${stuks === 1 ? 'stuk' : 'stuks'} toegevoegd aan je winkelmand`);
      if (!events) location.href = (window.Shopify?.routes?.root || '/') + 'cart';
    } catch (e) {
      uitgesteld?.resolve({ cart: null, detail: { didError: true } });
      this.#melding(this.#vertaalFout(e.message), true);
    } finally {
      knoppen.forEach((k) => k.classList.remove('is-bezig'));
      this.render();
    }
  }

  #vertaalFout(tekst) {
    const m = /You can only add (\d+) of (.+?) to the cart/i.exec(tekst || '') || /only (\d+) (?:items|of)/i.exec(tekst || '');
    if (m) return `Er is niet genoeg voorraad: van ${m[2] || 'één maat'} kun je nog maximaal ${m[1]} toevoegen. Pas het aantal aan.`;
    if (/sold out|uitverkocht/i.test(tekst)) return 'Een van de gekozen maten is inmiddels uitverkocht. Pas je keuze aan.';
    return 'Toevoegen lukte niet. Probeer het opnieuw of neem contact met ons op.';
  }

  #analytics(regels) {
    if (!regels.length) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    // Eigen eventnaam: Stape/Eyk pusht 'add_to_cart_stape' (bij meerdere regels maar één regel);
    // 'add_to_cart' zelf gebruiken we niet, zodat bestaande GTM-triggers niet dubbel tellen.
    window.dataLayer.push({
      event: 'mk_add_to_cart',
      ecommerce: {
        currency: 'EUR',
        value: regels.reduce((s, r) => s + (r.final_line_price ?? r.final_price * r.quantity), 0) / 100,
        items: regels.map((r) => ({
          item_id: r.sku || String(r.variant_id),
          item_name: r.product_title,
          item_brand: r.vendor,
          item_variant: r.variant_title,
          price: r.final_price / 100,
          quantity: r.quantity,
        })),
      },
    });
  }
}

if (!customElements.get('product-matrix')) customElements.define('product-matrix', ProductMatrix);
